<template>
  <el-drawer
    :title="drawerTitle"
    :visible.sync="visible"
    :direction="direction"
    :before-close="handleClose"
    :append-to-body="true"
    :close-on-click-modal="false"
    :size="width"
    @open="open">
    <div v-loading="loadingVisit" class="drawer__content scrollable only-y" :style="divHeight">
      <el-form ref="visitForm" :model="form" :rules="rules" label-position="top">
        <el-form-item label="Client" prop="customerId">
          <el-autocomplete
            v-if="isCustomerSelectionEnabled"
            v-model="planningCustomerQuery"
            :fetch-suggestions="querySearchCustomers"
            :trigger-on-focus="false"
            placeholder="Rechercher un client"
            style="width:100%"
            @select="handleCustomerSelect">
            <template slot-scope="{ item }">
              <div class="customer-option-item">
                <div class="customer-option-main">{{ customerDisplayName(item) }}</div>
                <div v-if="customerSecondaryLine(item)" class="customer-option-secondary">{{ customerSecondaryLine(item) }}</div>
              </div>
            </template>
          </el-autocomplete>
          <el-input v-else :value="resolvedCustomerName" disabled />
        </el-form-item>
        <el-form-item label="Représentant" prop="representativeId">
          <el-select
            v-model="form.representativeId"
            filterable
            :clearable="!isRepresentativeLocked"
            :disabled="isRepresentativeLocked"
            placeholder="Sélectionner un représentant"
            style="width:100%">
            <el-option
              v-for="representative in planningRepresentativeOptions"
              :key="`planning-rep-${representative.id}`"
              :label="representative.label"
              :value="representative.id" />
          </el-select>
        </el-form-item>
        <el-form-item :label="visitDateLabel" prop="visitDate">
          <el-date-picker
            v-model="form.visitDate"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd/MM/yyyy"
            :picker-options="visitDatePickerOptions"
            placeholder="Sélectionner une date"
            style="width:100%" />
        </el-form-item>
        <el-row v-if="isPlanningMode" :gutter="10">
          <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
            <el-form-item :label="planningTimeLabel">
              <el-time-picker
                v-model="form.nextVisitTime"
                value-format="HH:mm"
                format="HH:mm"
                placeholder="Sélectionner l'heure"
                style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
            <el-form-item label="Durée (minutes)">
              <el-input-number
                v-model="form.nextVisitDurationMinutes"
                :min="5"
                :step="5"
                :max="720"
                style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="Type de visite" prop="visitType">
          <el-radio-group v-model="form.visitType" class="visit-type-group">
            <el-radio-button
              v-for="visitType in visitTypes"
              :key="visitType.value"
              :label="visitType.value"
              class="visit-type-radio">
              <span class="visit-type-option">
                <i :class="visitType.icon" />
                <span>{{ $t(visitType.labelKey) }}</span>
              </span>
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Contacts visités" prop="visitedContactIds">
          <el-select
            v-model="form.visitedContactIds"
            multiple
            filterable
            collapse-tags
            placeholder="Sélectionner les contacts"
            style="width:100%">
            <el-option
              v-for="contact in availableContacts"
              :key="contact.id"
              :label="contactLabel(contact)"
              :value="contact.id" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isPlanningMode" label="Produits dégustés">
          <el-autocomplete
            v-model="tastedProductQuery"
            :fetch-suggestions="querySearchProducts"
            class="product-search-input"
            placeholder="Rechercher et ajouter un produit dégusté"
            @select="(product) => onProductAutocompleteSelect('tasted', product)">
            <template slot-scope="{ item }">
              <div class="product-option-item">
                <div class="product-option-main">{{ productMainLine(item) }}</div>
                <div v-if="productProviderName(item)" class="product-option-provider">{{ productProviderName(item) }}</div>
              </div>
            </template>
          </el-autocomplete>
          <div v-if="tastedProducts.length === 0" class="secondary-text fs-12 mt-5">
            Aucun produit sélectionné
          </div>
          <div v-for="product in tastedProducts" :key="`tasted-${product.puid}`" class="selected-product-item mt-5">
            <div class="selected-product-content">
              <div class="product-option-main">{{ productMainLine(product) }}</div>
              <div v-if="productProviderName(product)" class="product-option-provider">{{ productProviderName(product) }}</div>
            </div>
            <el-button
              type="text"
              icon="el-icon-close"
              @click="removeProductFromField('tasted', product.puid)" />
          </div>
          <el-button
            type="text"
            class="mt-5"
            :disabled="form.tastedProductPuids.length === 0"
            @click="addTastedProductsToInterested">
            Ajouter les produits dégustés aux produits intéressants
          </el-button>
        </el-form-item>
        <el-form-item v-if="!isPlanningMode" label="Produits qui intéressent le client">
          <el-autocomplete
            v-model="interestedProductQuery"
            :fetch-suggestions="querySearchProducts"
            class="product-search-input"
            placeholder="Rechercher et ajouter un produit intéressant"
            @select="(product) => onProductAutocompleteSelect('interested', product)">
            <template slot-scope="{ item }">
              <div class="product-option-item">
                <div class="product-option-main">{{ productMainLine(item) }}</div>
                <div v-if="productProviderName(item)" class="product-option-provider">{{ productProviderName(item) }}</div>
              </div>
            </template>
          </el-autocomplete>
          <div v-if="interestedProducts.length === 0" class="secondary-text fs-12 mt-5">
            Aucun produit sélectionné
          </div>
          <div v-for="product in interestedProducts" :key="`interested-${product.puid}`" class="selected-product-item mt-5">
            <div class="selected-product-content">
              <div class="product-option-main">{{ productMainLine(product) }}</div>
              <div v-if="productProviderName(product)" class="product-option-provider">{{ productProviderName(product) }}</div>
              <div class="selected-product-rating mt-5">
                <el-select v-model="form.interestRatings[product.puid]" placeholder="Niveau d'intérêt" size="mini" style="width:100%">
                  <el-option
                    v-for="rating in ratings"
                    :key="`selected-rating-${product.puid}-${rating.value}`"
                    :label="rating.label"
                    :value="rating.value">
                    <span class="rating-option">
                      <i :class="rating.icon" :style="{ color: rating.color }" />
                      <span>{{ rating.label }}</span>
                    </span>
                  </el-option>
                </el-select>
              </div>
            </div>
            <el-button
              type="text"
              icon="el-icon-close"
              @click="removeProductFromField('interested', product.puid)" />
          </div>
        </el-form-item>
        <el-form-item v-if="!isPlanningMode" label="Date de la prochaine visite">
          <el-date-picker
            v-model="form.nextVisitDate"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd/MM/yyyy"
            placeholder="Sélectionner une date"
            style="width:100%" />
        </el-form-item>
        <el-row v-if="!isPlanningMode" :gutter="10">
          <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
            <el-form-item label="Heure de rencontre (prochaine visite)">
              <el-time-picker
                v-model="form.nextVisitTime"
                value-format="HH:mm"
                format="HH:mm"
                placeholder="Sélectionner l'heure"
                style="width:100%"
                :disabled="!form.nextVisitDate" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
            <el-form-item label="Durée (minutes)">
              <el-input-number
                v-model="form.nextVisitDurationMinutes"
                :min="5"
                :step="5"
                :max="720"
                style="width:100%"
                :disabled="!form.nextVisitDate" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="Notes">
          <el-input
            v-model="form.notes"
            type="textarea"
            :rows="4"
            maxlength="4000"
            show-word-limit
            placeholder="Ajouter des notes sur la visite"
          />
        </el-form-item>
        <!-- <el-form-item v-if="!isPlanningMode && !isEditMode" label="Courriel récapitulatif">
          <el-switch
            v-model="form.sendVisitSummaryEmail"
            active-text="Envoyer un courriel récapitulatif au client"
            inactive-text="Ne pas envoyer de courriel"
          />
        </el-form-item> -->
      </el-form>
    </div>
    <div class="dialog-footer card-shadow--large">
      <el-button @click="handleClose">{{ $t('common.Cancel') }}</el-button>
      <el-button type="primary" :loading="saving" @click="save">{{ saveButtonLabel }}</el-button>
    </div>
  </el-drawer>
</template>
<script>
import axios from 'axios'
import _ from 'lodash'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import InventoryAPI from '@/services/API/inventory'
import CustomerAPI from '@/services/API/customer'
import VisitAPI from '@/services/API/visit'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'visit-creation-drawer',
  props: {
    customerId: {
      type: Number,
      default: null,
    },
    customerName: {
      type: String,
      default: '',
    },
    contacts: {
      type: Array,
      default() {
        return [];
      },
    },
    visible: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: 'rtl',
    },
    mode: {
      type: String,
      default: 'report',
    },
    visitToEdit: {
      type: Object,
      default: null,
    }
  },
  data() {
    const validateVisitDate = (rule, value, callback) => {
      if (value == null || value === '') {
        callback();
        return;
      }

      const selectedDate = new Date(`${value}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (Number.isNaN(selectedDate.getTime())) {
        callback(new Error('La date de visite est invalide'));
        return;
      }

      if (this.isPlanningMode) {
        if (selectedDate.getTime() < today.getTime()) {
          callback(new Error('La date de visite planifiée ne peut pas être dans le passé'));
          return;
        }
      } else if (selectedDate.getTime() > today.getTime()) {
        callback(new Error('La date de visite ne peut pas etre dans le futur'));
        return;
      }

      callback();
    };

    const validateRepresentative = (rule, value, callback) => {
      if (value == null || value === '') {
        callback(new Error('Le représentant est obligatoire'));
        return;
      }

      callback();
    };

    return {
      loadingProducts: false,
      loadingVisit: false,
      saving: false,
      productOptions: [],
      productOptionsByPuid: {},
      productSearchDebounceId: null,
      productSearchCancelSource: null,
      customerSearchDebounceId: null,
      tastedProductQuery: '',
      interestedProductQuery: '',
      planningCustomerQuery: '',
      planningContacts: [],
      form: {
        customerId: null,
        representativeId: null,
        plannedVisitId: null,
        visitDate: null,
        visitType: null,
        visitedContactIds: [],
        tastedProductPuids: [],
        interestedProductPuids: [],
        interestRatings: {},
        nextVisitDate: null,
        nextVisitTime: '09:00',
        nextVisitDurationMinutes: 30,
        notes: '',
        sendVisitSummaryEmail: false,
      },
      rules: {
        customerId: [
          { required: true, message: 'Le client est obligatoire', trigger: 'change' },
        ],
        representativeId: [
          { validator: validateRepresentative, trigger: 'change' },
        ],
        visitDate: [
          { required: true, message: 'La date de visite est obligatoire', trigger: 'change' },
          { validator: validateVisitDate, trigger: 'change' },
        ],
        visitType: [
          { required: true, message: 'Le type de visite est obligatoire', trigger: 'change' },
        ],
      },
      visitTypes: [
        { value: 'VISIT', labelKey: 'clients.visitTypeLabels.visit', icon: 'el-icon-office-building' },
        { value: 'CALL', labelKey: 'clients.visitTypeLabels.call', icon: 'el-icon-phone' },
        { value: 'EMAIL', labelKey: 'clients.visitTypeLabels.email', icon: 'el-icon-message' },
        { value: 'SMS', labelKey: 'clients.visitTypeLabels.sms', icon: 'el-icon-message' },
        { value: 'QUICK_CALL', labelKey: 'clients.visitTypeLabels.quickCall', icon: 'el-icon-phone-outline' },
        { value: 'PHONE_CALL', labelKey: 'clients.visitTypeLabels.phoneCall', icon: 'el-icon-phone' },
        { value: 'EMAIL-SMS', labelKey: 'clients.visitTypeLabels.emailSms', icon: 'el-icon-message' },
        { value: 'DELIVERY-PICK_UP', labelKey: 'clients.visitTypeLabels.deliveryPickUp', icon: 'el-icon-truck' },
        { value: 'DEGUSTATION', labelKey: 'clients.visitTypeLabels.degustation', icon: 'el-icon-goblet' },
        { value: 'OTHER', labelKey: 'clients.visitTypeLabels.other', icon: 'el-icon-more' },
      ],
      ratings: [
        { value: 'ORDER_IMMINENT', label: 'Commande imminente', icon: 'el-icon-lightning', color: '#67C23A' },
        { value: 'LIKELY_RESERVE', label: 'Réservera probablement', icon: 'el-icon-date', color: '#409EFF' },
        { value: 'VERY_INTERESTED', label: 'Très intéressé', icon: 'el-icon-star-on', color: '#2D8CF0' },
        { value: 'INTERESTED', label: 'Intéressé', icon: 'el-icon-star-on', color: '#66B1FF' },
        { value: 'CURIOUS', label: 'Curieux', icon: 'el-icon-view', color: '#E6A23C' },
        { value: 'NO_INTEREST', label: 'aucun intérêt', icon: 'el-icon-circle-close', color: '#F56C6C' },
      ],
      window: {
        width: 0,
        height: 0
      },
      plannedVisitPromptedCustomerIds: {},
    };
  },
  methods: {
    getTodayDate() {
      return dayjs().format('YYYY-MM-DD');
    },
    normalizeDateValue(value) {
      if (value == null || value === '') {
        return null;
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim();
        const isoDateMatch = trimmedValue.match(/^\d{4}-\d{2}-\d{2}/);
        if (isoDateMatch != null) {
          return isoDateMatch[0];
        }
      }

      const parsedDate = dayjs(value);
      if (!parsedDate.isValid()) {
        return null;
      }

      return parsedDate.format('YYYY-MM-DD');
    },
    normalizeTimeValue(value) {
      if (value == null || value === '') {
        return null;
      }
      const valueAsString = String(value).trim();
      const timeMatch = valueAsString.match(/(\d{2}):(\d{2})/);
      if (timeMatch == null) {
        return null;
      }
      return `${timeMatch[1]}:${timeMatch[2]}`;
    },
    parseTimeToMinutes(timeValue) {
      const normalizedTime = this.normalizeTimeValue(timeValue);
      if (normalizedTime == null) {
        return null;
      }
      const timeParts = normalizedTime.split(':');
      const hour = Number(timeParts[0]);
      const minute = Number(timeParts[1]);
      if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return null;
      }
      return (hour * 60) + minute;
    },
    formatMinutesToTime(minutesValue) {
      if (!Number.isFinite(minutesValue)) {
        return null;
      }
      const safeMinutes = Math.max(0, Math.min(1439, Math.round(minutesValue)));
      const hour = String(Math.floor(safeMinutes / 60)).padStart(2, '0');
      const minute = String(safeMinutes % 60).padStart(2, '0');
      return `${hour}:${minute}`;
    },
    computeDurationMinutes(startTimeValue, endTimeValue) {
      const startMinutes = this.parseTimeToMinutes(startTimeValue);
      const endMinutes = this.parseTimeToMinutes(endTimeValue);
      if (startMinutes == null || endMinutes == null) {
        return 30;
      }

      let duration = endMinutes - startMinutes;
      if (duration <= 0) {
        duration += 24 * 60;
      }
      if (!Number.isFinite(duration) || duration <= 0) {
        return 30;
      }
      return Math.max(5, duration);
    },
    toApiTimeValue(timeValue) {
      const normalizedTime = this.normalizeTimeValue(timeValue);
      if (normalizedTime == null) {
        return null;
      }
      return `${normalizedTime}:00`;
    },
    open() {
      this.reset();
      this.prefillForEdit();
      if (this.isPlanningMode && this.form.customerId == null && this.customerId != null) {
        this.form.customerId = this.customerId;
        this.planningCustomerQuery = this.customerName;
        this.loadPlanningContacts(this.customerId);
      }
      if (!this.isPlanningMode && !this.isEditMode && !this.isReportConversion() && this.form.customerId != null) {
        this.applyRepresentativeFromCustomerId(this.form.customerId, { force: true });
      }
      if (this.isCustomerSelectionEnabled && this.form.customerId != null) {
        this.loadPlanningContacts(this.form.customerId);
      }
    },
    isReportConversion() {
      return !this.isPlanningMode && _.toUpper(_.get(this.visitToEdit, 'status')) === 'PLANNED';
    },
    getVisitId(visit) {
      if (visit == null) {
        return null;
      }
      return visit.id
        || visit.visitId
        || visit.visit_id
        || visit.fk_visit_id
        || _.get(visit, 'data.id')
        || _.get(visit, 'visit.id')
        || null;
    },
    normalizeVisitTypeCode(value) {
      const rawValue = (value || '').toString().trim().toUpperCase();
      if (rawValue === 'QUICK_CALL' || rawValue === 'PHONE_CALL' || rawValue === 'CALL') {
        return rawValue === 'CALL' ? 'CALL' : rawValue;
      }
      if (rawValue === 'EMAIL-SMS' || rawValue === 'EMAIL' || rawValue === 'SMS') {
        if (rawValue === 'SMS') {
          return 'SMS';
        }
        return rawValue;
      }
      return rawValue;
    },
    extractVisitType(visit) {
      const rawType =
        (visit && visit.visitType && visit.visitType.code) ||
        (visit && visit.visit_type && visit.visit_type.code) ||
        (visit && visit.type && visit.type.code) ||
        (visit && visit.type && visit.type.value) ||
        (visit && visit.type && visit.type.name) ||
        (visit && visit.visitType && visit.visitType.value) ||
        (visit && visit.visit_type && visit.visit_type.value) ||
        (visit && visit.typeCode) ||
        (visit && visit.visitTypeCode) ||
        (visit && visit.visit_type_code) ||
        (visit && visit.fk_visit_type_code) ||
        (visit && visit.visitTypeLabel) ||
        (visit && visit.visit_type_label) ||
        (visit && visit.type) ||
        null;
      const normalizedType = this.normalizeVisitTypeCode(rawType);
      const supportedCodes = this.visitTypes.map((type) => type.value);
      if (supportedCodes.includes(normalizedType)) {
        return normalizedType;
      }
      return null;
    },
    normalizeSelectionId(value) {
      if (value == null || value === '') {
        return null;
      }
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && String(numericValue) === String(value).trim()) {
        return numericValue;
      }
      return value;
    },
    extractVisitedContactIds(visit) {
      const visitedContacts =
        (visit && visit.visitedContacts) ||
        (visit && visit.visited_contacts) ||
        (visit && visit.contacts) ||
        (visit && visit.customerContacts) ||
        [];

      const directContactIds =
        _.get(visit, 'visitedContactIds')
        || _.get(visit, 'visited_contact_ids')
        || _.get(visit, 'contactIds')
        || _.get(visit, 'contact_ids')
        || null;

      const idsFromDirectList = Array.isArray(directContactIds)
        ? directContactIds
        : (typeof directContactIds === 'string' && directContactIds.trim() !== ''
          ? directContactIds.split(',').map((idValue) => idValue.trim())
          : []);

      const idsFromContacts = Array.isArray(visitedContacts)
        ? visitedContacts
          .map((contact) => {
            if (contact == null) {
              return null;
            }
            if (typeof contact !== 'object') {
              return contact;
            }
            return contact.id || contact.contactId || contact.fk_contact_id || null;
          })
        : [];

      const singleContactId = _.get(visit, 'contactId') || _.get(visit, 'fk_contact_id') || null;
      const mergedIds = [
        ...idsFromDirectList,
        ...idsFromContacts,
        singleContactId,
      ]
        .map((idValue) => this.normalizeSelectionId(idValue))
        .filter((idValue) => idValue != null);

      return Array.from(new Set(mergedIds));
    },
    extractVisitDateSource(visit) {
      return this.getVisitDate(visit)
        || _.get(visit, 'visit_date')
        || _.get(visit, 'nextVisit')
        || _.get(visit, 'next_visit')
        || _.get(visit, 'nextVisitDate')
        || _.get(visit, 'next_visit_date')
        || _.get(visit, 'plannedDate')
        || _.get(visit, 'planned_date')
        || null;
    },
    extractVisitStartTime(visit) {
      return _.get(visit, 'startTime')
        || _.get(visit, 'start_time')
        || _.get(visit, 'nextVisitStartTime')
        || _.get(visit, 'next_visit_start_time')
        || _.get(visit, 'visitTime')
        || _.get(visit, 'visit_time')
        || _.get(visit, 'time')
        || null;
    },
    extractVisitEndTime(visit) {
      return _.get(visit, 'endTime')
        || _.get(visit, 'end_time')
        || _.get(visit, 'nextVisitEndTime')
        || _.get(visit, 'next_visit_end_time')
        || null;
    },
    extractVisitDurationMinutes(visit) {
      const explicitDuration = _.get(visit, 'nextVisitDurationMinutes')
        || _.get(visit, 'next_visit_duration_minutes')
        || _.get(visit, 'durationMinutes')
        || _.get(visit, 'duration_minutes')
        || null;
      const numericDuration = Number(explicitDuration);
      if (!Number.isNaN(numericDuration) && numericDuration > 0) {
        return numericDuration;
      }
      return this.computeDurationMinutes(this.extractVisitStartTime(visit), this.extractVisitEndTime(visit));
    },
    extractVisitNotes(visit) {
      return _.get(visit, 'notes')
        || _.get(visit, 'comment')
        || _.get(visit, 'description')
        || _.get(visit, 'memo')
        || '';
    },
    extractPlannedVisitId(visit) {
      return _.get(visit, 'plannedVisit.id') || _.get(visit, 'planned_visit.id') || _.get(visit, 'plannedVisitId') || _.get(visit, 'fk_planned_visit_id') || null;
    },
    getCurrentUserId() {
      const userIdFromGetter = _.get(this.$store, 'getters.getCurrentUserId');
      if (userIdFromGetter != null && String(userIdFromGetter).trim() !== '') {
        return userIdFromGetter;
      }

      const fallbackUserId = _.get(this.$store, 'getters.user.id');
      return fallbackUserId != null && String(fallbackUserId).trim() !== '' ? fallbackUserId : null;
    },
    getCurrentUserDisplayName() {
      const currentUser = _.get(this.$store, 'getters.user');
      if (currentUser == null) {
        return null;
      }

      const firstName = currentUser.firstName || currentUser.first_name || '';
      const lastName = currentUser.lastName || currentUser.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName !== '') {
        return fullName;
      }

      const fallbackLabel = currentUser.name || currentUser.username || currentUser.email || currentUser.work_email;
      return fallbackLabel != null && String(fallbackLabel).trim() !== '' ? String(fallbackLabel) : null;
    },
    extractCustomerRepresentativeId(customer) {
      const representativeId = _.get(customer, 'rep.id')
        || _.get(customer, 'repId')
        || _.get(customer, 'fk_rep_id')
        || _.get(customer, 'representative.id')
        || null;

      return representativeId != null && String(representativeId).trim() !== '' ? representativeId : null;
    },
    extractVisitRepresentativeId(visit) {
      const representativeId = _.get(visit, 'rep.id')
        || _.get(visit, 'repId')
        || _.get(visit, 'rep_id')
        || _.get(visit, 'fk_rep_id')
        || _.get(visit, 'fkRepId')
        || _.get(visit, 'userId')
        || null;

      return representativeId != null && String(representativeId).trim() !== '' ? representativeId : null;
    },
    applyRepresentativeFromCustomerId(customerId, { force = false } = {}) {
      if (customerId == null) {
        if (force || this.form.representativeId == null || this.form.representativeId === '') {
          this.form.representativeId = this.getCurrentUserId();
        }
        return Promise.resolve(this.form.representativeId);
      }

      return CustomerAPI.fetchCustomersV2({ id: customerId, paging: 'no', limit: 1 })
        .then((customers) => {
          const customer = Array.isArray(customers) ? customers[0] : customers;
          const customerRepresentativeId = this.extractCustomerRepresentativeId(customer);

          if (customerRepresentativeId != null) {
            this.form.representativeId = customerRepresentativeId;
            return customerRepresentativeId;
          }

          const fallbackRepresentativeId = this.getCurrentUserId();
          if (force || this.form.representativeId == null || this.form.representativeId === '') {
            this.form.representativeId = fallbackRepresentativeId;
          }
          return this.form.representativeId;
        })
        .catch(() => {
          const fallbackRepresentativeId = this.getCurrentUserId();
          if (force || this.form.representativeId == null || this.form.representativeId === '') {
            this.form.representativeId = fallbackRepresentativeId;
          }
          return this.form.representativeId;
        });
    },
    rememberProductsFromVisit(products) {
      if (!Array.isArray(products) || products.length === 0) {
        return;
      }
      this.rememberProductOptions(products);
    },
    hasTruthyFlag(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },
    populateFormFromVisit(visit) {
      this.form.representativeId = this.extractVisitRepresentativeId(visit) || this.getCurrentUserId();
      this.form.plannedVisitId = this.isReportConversion()
        ? this.getVisitId(visit)
        : this.extractPlannedVisitId(visit);
      const visitDateSource = this.extractVisitDateSource(visit);
      if (this.isReportConversion()) {
        this.form.visitDate = this.getTodayDate();
      } else {
        this.form.visitDate = this.normalizeDateValue(visitDateSource) || this.getTodayDate();
      }
      this.form.visitType = this.extractVisitType(visit);
      this.form.visitedContactIds = this.extractVisitedContactIds(visit);
      this.form.nextVisitDate = this.normalizeDateValue(_.get(visit, 'nextVisitDate') || _.get(visit, 'next_visit_date') || null);
      this.form.nextVisitTime = this.normalizeTimeValue(this.extractVisitStartTime(visit)) || '09:00';
      this.form.nextVisitDurationMinutes = this.extractVisitDurationMinutes(visit);
      this.form.notes = this.extractVisitNotes(visit);

      const products = Array.isArray(visit.products) ? visit.products : [];
      this.rememberProductsFromVisit(products);

      const tastedProductPuids = [];
      const interestedProductPuids = [];
      const interestRatings = {};

      products.forEach((product) => {
        if (product == null || product.puid == null) {
          return;
        }
        const isInterested = this.hasTruthyFlag(product.interested);
        const isTasted = this.hasTruthyFlag(product.tasted);

        if (isTasted) {
          tastedProductPuids.push(product.puid);
        }
        if (isInterested) {
          interestedProductPuids.push(product.puid);
          interestRatings[product.puid] = product.rating || 'CURIOUS';
        }

        if (!isTasted && !isInterested) {
          tastedProductPuids.push(product.puid);
        }
      });

      this.form.tastedProductPuids = Array.from(new Set(tastedProductPuids));
      this.form.interestedProductPuids = Array.from(new Set(interestedProductPuids));
      this.form.interestRatings = interestRatings;
    },
    prefillForEdit() {
      if (this.visitToEdit == null) {
        return;
      }
      const baseVisit = _.isPlainObject(this.visitToEdit) ? this.visitToEdit : null;
      const visitId = this.visitId;
      if (visitId == null) {
        this.populateFormFromVisit(baseVisit || {});
        return;
      }

      if (baseVisit != null) {
        this.populateFormFromVisit(baseVisit);
      }

      this.loadingVisit = true;
      VisitAPI.getVisit(visitId)
        .then((visit) => {
          const resolvedVisit = this.resolveVisitPayload(visit);
          const visitForForm = this.mergeVisitForPrefill(baseVisit, resolvedVisit);
          this.populateFormFromVisit(visitForForm || baseVisit || {});
        })
        .catch(() => {
          this.populateFormFromVisit(baseVisit || {});
          this.$message.error('Impossible de charger la visite a modifier.');
        })
        .finally(() => {
          this.loadingVisit = false;
        });
    },
    fetchProducts(query) {
      if (this.productSearchCancelSource != null) {
        this.productSearchCancelSource.cancel('Cancelled by a new product search.');
      }
      this.productSearchCancelSource = axios.CancelToken.source();
      this.loadingProducts = true;
      return InventoryAPI.fetchProductsV2(
        { paging: 'no', active: true, limit: 25, searchTerms: query },
        null,
        { cancelToken: this.productSearchCancelSource.token }
      )
        .then((products) => {
          this.productOptions = Array.isArray(products) ? products : [];
          this.rememberProductOptions(this.productOptions);
          return this.productOptions;
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return [];
          }
          this.productOptions = [];
          this.$message.error('Impossible de charger les produits d’inventaire.');
          return [];
        })
        .finally(() => {
          this.productSearchCancelSource = null;
          this.loadingProducts = false;
        })
    },
    customerDisplayName(customer) {
      if (customer == null) {
        return '';
      }

      const usual = [customer.usual_name, customer.usualName]
        .map((value) => (value == null ? '' : String(value).trim()))
        .find((value) => value !== '');
      const name = [customer.name]
        .map((value) => (value == null ? '' : String(value).trim()))
        .find((value) => value !== '');

      if (customer.company != null) {
        return usual || name || `Client #${customer.id}`;
      }
      return name || usual || `Client #${customer.id}`;
    },
    customerSecondaryLine(customer) {
      if (customer == null) {
        return '';
      }
      const parts = [];
      if (customer.city) {
        parts.push(customer.city);
      }
      if (customer.rep && (customer.rep.name || customer.rep.first_name || customer.rep.last_name)) {
        parts.push(this.contactLabel(customer.rep));
      }
      return parts.join(' · ');
    },
    querySearchCustomers(queryString, cb) {
      const query = (queryString || '').trim();
      if (this.customerSearchDebounceId != null) {
        clearTimeout(this.customerSearchDebounceId);
      }

      if (query === '') {
        cb([]);
        return;
      }

      this.customerSearchDebounceId = setTimeout(() => {
        CustomerAPI.fetchSearchedCustomersV2({ paging: 'no', active: true, limit: 25, searchTerms: query })
          .then((customers) => {
            cb(Array.isArray(customers) ? customers : []);
          })
          .catch(() => {
            cb([]);
            this.$message.error('Impossible de charger les clients.');
          });
      }, 250);
    },
    normalizeVisitList(visitsResponse) {
      if (Array.isArray(visitsResponse)) {
        return visitsResponse;
      }
      if (Array.isArray(_.get(visitsResponse, 'items'))) {
        return _.get(visitsResponse, 'items');
      }
      if (Array.isArray(_.get(visitsResponse, 'results'))) {
        return _.get(visitsResponse, 'results');
      }
      if (Array.isArray(_.get(visitsResponse, 'list'))) {
        return _.get(visitsResponse, 'list');
      }
      if (Array.isArray(_.get(visitsResponse, 'rows'))) {
        return _.get(visitsResponse, 'rows');
      }
      if (Array.isArray(_.get(visitsResponse, 'data'))) {
        return _.get(visitsResponse, 'data');
      }
      if (Array.isArray(_.get(visitsResponse, 'data.list'))) {
        return _.get(visitsResponse, 'data.list');
      }
      if (Array.isArray(_.get(visitsResponse, 'data.items'))) {
        return _.get(visitsResponse, 'data.items');
      }
      return [];
    },
    resolveVisitPayload(visitResponse) {
      if (_.isPlainObject(visitResponse)) {
        const candidateKeys = ['data.visit', 'data.item', 'data.result', 'visit', 'item', 'result'];
        for (let index = 0; index < candidateKeys.length; index += 1) {
          const candidate = _.get(visitResponse, candidateKeys[index]);
          if (_.isPlainObject(candidate)) {
            return candidate;
          }
        }
        return visitResponse;
      }

      if (Array.isArray(visitResponse) && visitResponse.length > 0 && _.isPlainObject(visitResponse[0])) {
        return visitResponse[0];
      }

      return null;
    },
    mergeVisitForPrefill(baseVisit, fetchedVisit) {
      const safeBaseVisit = _.isPlainObject(baseVisit) ? baseVisit : {};
      const safeFetchedVisit = _.isPlainObject(fetchedVisit) ? fetchedVisit : {};
      const mergedVisit = { ...safeBaseVisit, ...safeFetchedVisit };

      const hasDateInMerged = this.getVisitDate(mergedVisit) != null
        || _.get(mergedVisit, 'nextVisitDate') != null
        || _.get(mergedVisit, 'next_visit_date') != null;

      if (!hasDateInMerged) {
        if (_.get(safeBaseVisit, 'date') != null) mergedVisit.date = _.get(safeBaseVisit, 'date');
        if (_.get(safeBaseVisit, 'visitDate') != null) mergedVisit.visitDate = _.get(safeBaseVisit, 'visitDate');
        if (_.get(safeBaseVisit, 'nextVisitDate') != null) mergedVisit.nextVisitDate = _.get(safeBaseVisit, 'nextVisitDate');
        if (_.get(safeBaseVisit, 'next_visit_date') != null) mergedVisit.next_visit_date = _.get(safeBaseVisit, 'next_visit_date');
      }

      if (this.extractVisitType(mergedVisit) == null) {
        if (_.get(safeBaseVisit, 'visitType') != null) mergedVisit.visitType = _.get(safeBaseVisit, 'visitType');
        if (_.get(safeBaseVisit, 'visit_type') != null) mergedVisit.visit_type = _.get(safeBaseVisit, 'visit_type');
        if (_.get(safeBaseVisit, 'type') != null) mergedVisit.type = _.get(safeBaseVisit, 'type');
        if (_.get(safeBaseVisit, 'typeCode') != null) mergedVisit.typeCode = _.get(safeBaseVisit, 'typeCode');
      }

      if (this.extractVisitStartTime(mergedVisit) == null) {
        if (_.get(safeBaseVisit, 'startTime') != null) mergedVisit.startTime = _.get(safeBaseVisit, 'startTime');
        if (_.get(safeBaseVisit, 'start_time') != null) mergedVisit.start_time = _.get(safeBaseVisit, 'start_time');
        if (_.get(safeBaseVisit, 'nextVisitStartTime') != null) mergedVisit.nextVisitStartTime = _.get(safeBaseVisit, 'nextVisitStartTime');
        if (_.get(safeBaseVisit, 'next_visit_start_time') != null) mergedVisit.next_visit_start_time = _.get(safeBaseVisit, 'next_visit_start_time');
      }

      if (this.extractVisitedContactIds(mergedVisit).length === 0) {
        if (_.get(safeBaseVisit, 'visitedContacts') != null) mergedVisit.visitedContacts = _.get(safeBaseVisit, 'visitedContacts');
        if (_.get(safeBaseVisit, 'visited_contacts') != null) mergedVisit.visited_contacts = _.get(safeBaseVisit, 'visited_contacts');
        if (_.get(safeBaseVisit, 'contacts') != null) mergedVisit.contacts = _.get(safeBaseVisit, 'contacts');
        if (_.get(safeBaseVisit, 'visitedContactIds') != null) mergedVisit.visitedContactIds = _.get(safeBaseVisit, 'visitedContactIds');
      }

      if (this.extractVisitNotes(mergedVisit) === '') {
        if (_.get(safeBaseVisit, 'notes') != null) mergedVisit.notes = _.get(safeBaseVisit, 'notes');
        if (_.get(safeBaseVisit, 'comment') != null) mergedVisit.comment = _.get(safeBaseVisit, 'comment');
      }

      return mergedVisit;
    },
    isPlannedVisit(visit) {
      return _.toUpper(_.get(visit, 'status')) === 'PLANNED';
    },
    getVisitDate(visit) {
      return _.get(visit, 'date')
        || _.get(visit, 'visitDate')
        || _.get(visit, 'visit_date')
        || _.get(visit, 'visitedAt')
        || _.get(visit, 'created_at')
        || _.get(visit, 'nextVisit')
        || _.get(visit, 'nextVisitDate')
        || _.get(visit, 'next_visit_date')
        || null;
    },
    getLinkedPlannedVisitId(visit) {
      return _.get(visit, 'plannedVisit.id') || _.get(visit, 'planned_visit.id') || _.get(visit, 'plannedVisitId') || _.get(visit, 'fk_planned_visit_id') || null;
    },
    toComparableId(value) {
      if (value == null || value === '') {
        return null;
      }
      return String(value);
    },
    formatPromptVisitDate(visit) {
      const rawDate = this.getVisitDate(visit);
      const normalizedDate = this.normalizeDateValue(rawDate);
      if (normalizedDate == null) {
        return 'date inconnue';
      }
      const timeValue = this.normalizeTimeValue(_.get(visit, 'startTime') || _.get(visit, 'start_time'));
      if (timeValue == null) {
        return normalizedDate;
      }
      return `${normalizedDate} ${timeValue}`;
    },
    getSuggestedPlannedVisitWithoutReport(visits) {
      const safeVisits = Array.isArray(visits) ? visits.filter((visit) => _.isPlainObject(visit)) : [];
      const plannedVisits = safeVisits.filter((visit) => this.isPlannedVisit(visit));
      if (plannedVisits.length === 0) {
        return null;
      }

      const linkedPlannedVisitIds = new Set(
        safeVisits
          .filter((visit) => !this.isPlannedVisit(visit))
          .map((visit) => this.toComparableId(this.getLinkedPlannedVisitId(visit)))
          .filter((value) => value != null)
      );

      const unreportedPlannedVisits = plannedVisits.filter((visit) => {
        const visitId = this.toComparableId(this.getVisitId(visit));
        if (visitId == null) {
          return false;
        }
        return !linkedPlannedVisitIds.has(visitId);
      });

      if (unreportedPlannedVisits.length === 0) {
        return null;
      }

      const today = new Date();
      const todayTs = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

      const withTs = unreportedPlannedVisits.map((visit) => {
        const normalizedDate = this.normalizeDateValue(this.getVisitDate(visit));
        const visitDate = normalizedDate ? new Date(`${normalizedDate}T00:00:00`) : null;
        const ts = visitDate != null && !Number.isNaN(visitDate.getTime()) ? visitDate.getTime() : null;
        return { visit, ts };
      });

      const currentMonthVisits = withTs
        .filter((item) => item.ts != null)
        .filter((item) => {
          const date = new Date(item.ts);
          return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
        })
        .sort((a, b) => Math.abs(a.ts - todayTs) - Math.abs(b.ts - todayTs));
      if (currentMonthVisits.length > 0) {
        return currentMonthVisits[0].visit;
      }

      const futureVisits = withTs
        .filter((item) => item.ts != null && item.ts >= todayTs)
        .sort((a, b) => a.ts - b.ts);
      if (futureVisits.length > 0) {
        return futureVisits[0].visit;
      }

      const pastVisits = withTs
        .filter((item) => item.ts != null && item.ts < todayTs)
        .sort((a, b) => b.ts - a.ts);
      if (pastVisits.length > 0) {
        return pastVisits[0].visit;
      }

      return unreportedPlannedVisits[0];
    },
    findPlannedVisitWithoutReportForCustomer(customerId) {
      if (customerId == null) {
        return Promise.resolve(null);
      }
      return VisitAPI.fetchVisits({ paging: 'no', customerId })
        .then((visitsResponse) => {
          const visits = this.normalizeVisitList(visitsResponse);
          return this.getSuggestedPlannedVisitWithoutReport(visits);
        })
        .catch(() => null);
    },
    maybeAssociateWithPlannedVisitOnCustomerSelect(customer) {
      if (!this.shouldPromptPlannedVisitAssociationOnCustomerSelect) {
        return Promise.resolve();
      }

      const customerId = _.get(customer, 'id');
      if (customerId == null) {
        this.form.plannedVisitId = null;
        return Promise.resolve();
      }

      const customerKey = String(customerId);
      if (this.plannedVisitPromptedCustomerIds[customerKey] === true) {
        return Promise.resolve();
      }
      this.$set(this.plannedVisitPromptedCustomerIds, customerKey, true);

      return this.findPlannedVisitWithoutReportForCustomer(customerId)
        .then((suggestedPlannedVisit) => {
          if (suggestedPlannedVisit == null) {
            this.form.plannedVisitId = null;
            return;
          }

          const visitDateLabel = this.formatPromptVisitDate(suggestedPlannedVisit);
          const customerLabel = this.customerDisplayName(customer) || `Client #${customerId}`;

          return this.$confirm(
            this.$i18n.t('clients.visitsList.reportAssociationPromptWithCustomer', { customer: customerLabel, date: visitDateLabel }),
            this.$i18n.t('clients.visitsList.reportAssociationPromptTitle'),
            {
              confirmButtonText: 'Oui',
              cancelButtonText: 'Non',
              type: 'info',
            }
          )
            .then(() => {
              this.form.plannedVisitId = this.getVisitId(suggestedPlannedVisit);
              if (this.form.visitType == null) {
                this.form.visitType = this.extractVisitType(suggestedPlannedVisit);
              }
              if (this.form.visitedContactIds.length === 0) {
                this.form.visitedContactIds = this.extractVisitedContactIds(suggestedPlannedVisit);
              }
            })
            .catch(() => {
              this.form.plannedVisitId = null;
            });
        });
    },
    loadPlanningContacts(customerId) {
      if (customerId == null) {
        this.planningContacts = [];
        return Promise.resolve([]);
      }

      return CustomerAPI.fetchCustomerContacts(customerId)
        .then((contacts) => {
          this.planningContacts = Array.isArray(contacts) ? contacts : [];
          if (this.planningContacts.length === 1 && this.planningContacts[0] != null) {
            this.form.visitedContactIds = [this.planningContacts[0].id];
          } else {
            this.form.visitedContactIds = [];
          }
          return this.planningContacts;
        })
        .catch(() => {
          this.planningContacts = [];
          this.form.visitedContactIds = [];
          this.$message.error('Impossible de charger les contacts du client.');
          return [];
        });
    },
    handleCustomerSelect(customer) {
      if (customer == null) {
        this.form.customerId = null;
        this.form.representativeId = this.getCurrentUserId();
        this.form.plannedVisitId = null;
        this.planningCustomerQuery = '';
        this.planningContacts = [];
        this.form.visitedContactIds = [];
        return;
      }

      this.form.customerId = customer.id;
      this.form.representativeId = this.extractCustomerRepresentativeId(customer) || this.getCurrentUserId();
      this.planningCustomerQuery = this.customerDisplayName(customer);
      this.loadPlanningContacts(customer.id)
        .finally(() => this.maybeAssociateWithPlannedVisitOnCustomerSelect(customer))
        .finally(() => {
          this.$nextTick(() => {
            if (this.$refs.visitForm) {
              this.$refs.visitForm.validateField('customerId');
              this.$refs.visitForm.validateField('representativeId');
            }
          });
        });
    },
    querySearchProducts(queryString, cb) {
      const query = (queryString || '').trim();

      if (this.productSearchDebounceId != null) {
        clearTimeout(this.productSearchDebounceId);
      }

      if (query === '') {
        if (this.productSearchCancelSource != null) {
          this.productSearchCancelSource.cancel('Cancelled because query is empty.');
          this.productSearchCancelSource = null;
        }
        this.loadingProducts = false;
        this.productOptions = [];
        cb([]);
        return;
      }

      this.productSearchDebounceId = setTimeout(() => {
        this.fetchProducts(query)
          .then((products) => cb(products))
          .catch(() => cb([]));
      }, 350);
    },
    onProductAutocompleteSelect(field, product) {
      if (product == null || product.puid == null) {
        return;
      }

      this.rememberProductOptions([product]);

      if (field === 'tasted') {
        this.form.tastedProductPuids = Array.from(new Set([
          ...this.form.tastedProductPuids,
          product.puid,
        ]));
        this.tastedProductQuery = '';
        return;
      }

      const nextInterested = Array.from(new Set([
        ...this.form.interestedProductPuids,
        product.puid,
      ]));
      this.form.interestedProductPuids = nextInterested;
      this.handleInterestedProductsChange(nextInterested);
      this.interestedProductQuery = '';
    },
    contactLabel(contact) {
      if (contact == null) {
        return '';
      }
      const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
      if (fullName !== '') {
        return fullName;
      }
      return contact.email || `Contact #${contact.id}`;
    },
    productLabel(product) {
      if (product == null) {
        return '';
      }
      return this.productMainLine(product);
    },
    productName(product) {
      return product.name || (product.product && product.product.name) || product.puid;
    },
    productProviderName(product) {
      return (
        (product.provider && (product.provider.usualName || product.provider.usual_name || product.provider.name)) ||
        (product.product && product.product.provider && (product.product.provider.usualName || product.product.provider.usual_name || product.product.provider.name)) ||
        null
      );
    },
    productUvc(product) {
      return product.uvc || (product.product && product.product.uvc) || null;
    },
    productLblFormat(product) {
      return (
        product.lblFormat ||
        product.format ||
        (product.product && (product.product.lblFormat || product.product.format)) ||
        null
      );
    },
    productMainLine(product) {
      const name = this.productName(product);
      const uvc = this.productUvc(product);
      const lblFormat = this.productLblFormat(product);
      const details = [];

      if (uvc != null && uvc !== '' && lblFormat) {
        details.push(`${uvc} x ${lblFormat}`);
      } else if (uvc != null && uvc !== '') {
        details.push(`${uvc}`);
      } else if (lblFormat) {
        details.push(lblFormat);
      }

      if (details.length === 0) {
        return name;
      }

      return `${name} - ${details.join(' · ')}`;
    },
    rememberProductOptions(products) {
      products.forEach((product) => {
        if (product != null && product.puid != null) {
          this.$set(this.productOptionsByPuid, product.puid, product);
        }
      });
    },
    interestedProductName(puid) {
      const product = this.productOptionsByPuid[puid] || this.productOptions.find((p) => p.puid === puid);
      return this.productLabel(product) || puid;
    },
    handleInterestedProductsChange(selected) {
      const nextRatings = {};
      selected.forEach((puid) => {
        nextRatings[puid] = this.form.interestRatings[puid] || 'CURIOUS';
      });
      this.form.interestRatings = nextRatings;
    },
    addTastedProductsToInterested() {
      const merged = Array.from(new Set([
        ...this.form.interestedProductPuids,
        ...this.form.tastedProductPuids,
      ]));

      this.form.interestedProductPuids = merged;
      this.handleInterestedProductsChange(merged);
    },
    removeProductFromField(field, puid) {
      if (field === 'tasted') {
        this.form.tastedProductPuids = this.form.tastedProductPuids.filter((value) => value !== puid);
        return;
      }

      const nextInterested = this.form.interestedProductPuids.filter((value) => value !== puid);
      this.form.interestedProductPuids = nextInterested;
      this.handleInterestedProductsChange(nextInterested);
    },
    reset() {
      this.form.customerId = this.customerId;
      this.form.representativeId = this.getCurrentUserId();
      this.form.plannedVisitId = null;
      this.form.visitDate = this.isPlanningMode ? null : this.getTodayDate();
      this.form.visitType = null;
      this.form.visitedContactIds = this.availableContacts.length === 1 && this.availableContacts[0] != null
        ? [this.availableContacts[0].id]
        : [];
      this.form.tastedProductPuids = [];
      this.form.interestedProductPuids = [];
      this.form.interestRatings = {};
      this.form.nextVisitDate = null;
      this.form.nextVisitTime = '09:00';
      this.form.nextVisitDurationMinutes = 30;
      this.form.notes = '';
      this.form.sendVisitSummaryEmail = false;
      this.productOptions = [];
      this.productOptionsByPuid = {};
      this.tastedProductQuery = '';
      this.interestedProductQuery = '';
      this.planningCustomerQuery = this.customerId != null ? this.customerName : '';
      this.planningContacts = this.customerId != null
        ? (Array.isArray(this.contacts) ? this.contacts : [])
        : [];
      this.plannedVisitPromptedCustomerIds = {};
      if (this.productSearchDebounceId != null) {
        clearTimeout(this.productSearchDebounceId);
        this.productSearchDebounceId = null;
      }
      if (this.customerSearchDebounceId != null) {
        clearTimeout(this.customerSearchDebounceId);
        this.customerSearchDebounceId = null;
      }
      if (this.productSearchCancelSource != null) {
        this.productSearchCancelSource.cancel('Cancelled by form reset.');
        this.productSearchCancelSource = null;
      }
      if (this.$refs.visitForm) {
        this.$refs.visitForm.clearValidate();
      }
    },
    handleClose() {
      this.$emit('close');
    },
    loadDraft() {
      this.reset();
      this.prefillForEdit();
      if (this.isPlanningMode && this.form.customerId == null && this.customerId != null) {
        this.form.customerId = this.customerId;
        this.planningCustomerQuery = this.customerName;
        this.loadPlanningContacts(this.customerId);
      }
      if (!this.isPlanningMode && !this.isEditMode && !this.isReportConversion() && this.form.customerId != null) {
        this.applyRepresentativeFromCustomerId(this.form.customerId, { force: true });
      }
      if (this.isCustomerSelectionEnabled && this.form.customerId != null) {
        this.loadPlanningContacts(this.form.customerId);
      }
    },
    save() {
      this.$refs.visitForm.validate((valid) => {
        if (!valid) {
          return;
        }
        this.saving = true;

        const hasNextVisitDate = !this.isPlanningMode && this.form.nextVisitDate != null && this.form.nextVisitDate !== '';
        const useTimeValues = this.isPlanningMode || hasNextVisitDate;
        const startMinutes = this.parseTimeToMinutes(this.form.nextVisitTime);
        const normalizedDuration = Math.max(5, Number(this.form.nextVisitDurationMinutes) || 30);
        const endMinutes = startMinutes != null ? startMinutes + normalizedDuration : null;
        const normalizedStartTime = startMinutes != null ? this.formatMinutesToTime(startMinutes) : null;
        const normalizedEndTime = endMinutes != null ? this.formatMinutesToTime(endMinutes % (24 * 60)) : null;

        const payload = {
          customerId: this.resolvedCustomerId,
          repId: this.form.representativeId,
          plannedVisitId: this.isPlanningMode ? null : this.form.plannedVisitId,
          visitDate: this.normalizeDateValue(this.form.visitDate),
          visitType: this.form.visitType,
          visitedContactIds: this.form.visitedContactIds,
          status: this.isPlanningMode ? 'PLANNED' : 'REPORTED',
          tastedProductPuids: this.isPlanningMode ? [] : this.form.tastedProductPuids,
          interestedProducts: this.isPlanningMode ? [] : this.form.interestedProductPuids.map((puid) => ({
            puid,
            rating: this.form.interestRatings[puid] || 'CURIOUS',
          })),
          nextVisitDate: this.isPlanningMode ? null : this.normalizeDateValue(this.form.nextVisitDate),
          startTime: useTimeValues ? this.toApiTimeValue(normalizedStartTime) : null,
          endTime: useTimeValues ? this.toApiTimeValue(normalizedEndTime) : null,
          notes: this.form.notes,
          sendVisitSummaryEmail: !this.isPlanningMode && this.form.sendVisitSummaryEmail === true,
        };
        const shouldUpdateExistingVisit = this.isEditMode && this.visitId != null && !this.isReportConversion();
        const savePromise = shouldUpdateExistingVisit
          ? VisitAPI.updateVisit(this.visitId, payload)
          : VisitAPI.createVisit(payload);

        return savePromise
          .then((savedVisit) => {
            this.$message.success(this.isEditMode
              ? 'La visite a ete modifiee avec succes.'
              : 'La visite a ete enregistree avec succes.');
            this.$emit('saved', savedVisit, {
              plannedVisitId: this.form.plannedVisitId,
              isReportConversion: this.isReportConversion(),
            });
            this.handleClose();
          })
          .catch(() => {
            this.$message.error(this.isEditMode
              ? 'Une erreur est survenue lors de la modification de la visite.'
              : 'Une erreur est survenue lors de l\'enregistrement de la visite.');
          })
          .finally(() => {
            this.saving = false;
          })
      });
    },
    handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    },
  },
  computed: {
    visitDatePickerOptions() {
      const isPlanningMode = this.isPlanningMode;
      return {
        disabledDate(date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return isPlanningMode
            ? date.getTime() < today.getTime()
            : date.getTime() > today.getTime();
        },
      };
    },
    isPlanningMode() {
      return String(this.mode).toLowerCase() === 'planning';
    },
    isCustomerSelectionEnabled() {
      if (this.isPlanningMode) {
        return true;
      }
      return !this.isEditMode && !this.isReportConversion() && this.customerId == null;
    },
    shouldPromptPlannedVisitAssociationOnCustomerSelect() {
      return !this.isPlanningMode && !this.isEditMode && !this.isReportConversion() && this.customerId == null;
    },
    resolvedCustomerId() {
      if (this.customerId != null) {
        return this.customerId;
      }
      return this.form.customerId;
    },
    resolvedCustomerName() {
      if (this.customerId != null) {
        return this.customerName;
      }
      return this.planningCustomerQuery || '';
    },
    visitId() {
      return this.getVisitId(this.visitToEdit);
    },
    isEditMode() {
      return this.visitId != null;
    },
    isRepresentativeLocked() {
      return this.isReportConversion();
    },
    drawerTitle() {
      if (this.isPlanningMode) {
        return this.isEditMode ? 'Modifier une visite planifiée' : 'Planifier une visite';
      }
      return this.isEditMode ? 'Modifier une visite' : 'Enregistrer une visite';
    },
    saveButtonLabel() {
      if (this.isPlanningMode) {
        return this.isEditMode ? 'Enregistrer la planification' : 'Planifier la visite';
      }
      return this.isEditMode ? 'Enregistrer les modifications' : 'Enregistrer la visite';
    },
    visitDateLabel() {
      return this.isPlanningMode ? 'Date de la visite planifiée' : 'Date de visite';
    },
    planningTimeLabel() {
      return 'Heure de la visite';
    },
    tastedProducts() {
      return this.form.tastedProductPuids.map((puid) => {
        return this.productOptionsByPuid[puid] || { puid, name: puid };
      });
    },
    interestedProducts() {
      return this.form.interestedProductPuids.map((puid) => {
        return this.productOptionsByPuid[puid] || { puid, name: puid };
      });
    },
    availableContacts() {
      if (this.isPlanningMode) {
        return this.planningContacts;
      }
      if (this.customerId == null) {
        return this.planningContacts;
      }
      return this.contacts;
    },
    planningRepresentativeOptions() {
      const reps = _.get(this.$store, "getters['users/filterUserWithRoleRep']", []);
      const options = Array.isArray(reps)
        ? reps
          .map((representative) => {
            const id = representative && representative.id != null ? representative.id : null;
            if (id == null) {
              return null;
            }

            const firstName = representative.firstName || representative.first_name || '';
            const lastName = representative.lastName || representative.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();
            const fallbackLabel = representative.name || representative.username || representative.email;

            return {
              id,
              label: fullName !== '' ? fullName : (fallbackLabel || `Représentant #${id}`),
            };
          })
          .filter((option) => option != null)
        : [];

      const currentUserId = this.getCurrentUserId();
      if (currentUserId != null && !options.some((option) => String(option.id) === String(currentUserId))) {
        options.unshift({
          id: currentUserId,
          label: this.getCurrentUserDisplayName() || `Représentant #${currentUserId}`,
        });
      }

      return options;
    },
    divHeight() {
      return `height :${this.window.height-130}px`
    },
    width() {
      if (this.window.width > 1024) {
        return '50%';
      }
      return '100%';
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize();
    this.$store.dispatch('users/fetchAdminUsers').catch(() => {
      // Keep form usable even if representative list cannot be loaded.
    });
  },
  watch: {
    customerId(newCustomerId) {
      if (this.isPlanningMode && newCustomerId != null && this.form.customerId == null) {
        this.form.customerId = newCustomerId;
        this.planningCustomerQuery = this.customerName;
        this.loadPlanningContacts(newCustomerId);
      }
      if (!this.isPlanningMode) {
        this.form.customerId = newCustomerId;
        if (newCustomerId != null) {
          this.planningCustomerQuery = this.customerName;
          this.planningContacts = Array.isArray(this.contacts) ? this.contacts : [];
        }
        if (!this.isReportConversion()) {
          this.applyRepresentativeFromCustomerId(newCustomerId, { force: true });
        }
      }
    },
    visitToEdit() {
      if (this.visible) {
        this.prefillForEdit();
      }
    },
    mode() {
      if (this.visible) {
        this.prefillForEdit();
      }
    },
    visible(isVisible) {
      if (isVisible) {
        this.loadDraft();
      }
    },
  },
  destroyed() {
    if (this.productSearchDebounceId != null) {
      clearTimeout(this.productSearchDebounceId);
      this.productSearchDebounceId = null;
    }
    if (this.customerSearchDebounceId != null) {
      clearTimeout(this.customerSearchDebounceId);
      this.customerSearchDebounceId = null;
    }
    if (this.productSearchCancelSource != null) {
      this.productSearchCancelSource.cancel('Cancelled on component destroy.');
      this.productSearchCancelSource = null;
    }
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>
<style lang="scss" scoped>
.dialog-footer{
  position: fixed;
  display: block;
  bottom: 0px;
  width: 100%;
  background-color: #f3f7fa;
  height:60px;
  padding-left:40px;
  padding-top:10px;
  border-top:1px solid #E4E7ED
}

.drawer__content {
  margin-left:40px;
  margin-right:20px;
}

.visit-type-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.visit-type-group {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.visit-type-radio {
  margin-right: 10px;
  margin-bottom: 8px;
}

.product-option-item {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  margin: 10px 0;
}

.product-option-main {
  font-size: 13px;
}

.product-option-provider {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
}

.selected-product-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fafafa;
}

.selected-product-content {
  min-width: 0;
  margin-right: 10px;
}

.selected-product-content .product-option-item {
  line-height: 1.1;
  margin: 0;
}

.selected-product-content .product-option-main {
  margin-bottom: 0;
  line-height: 1.1;
}

.selected-product-content .product-option-provider {
  margin-top: 1px;
  line-height: 1.1;
}

.product-search-input {
  width: 100%;
}

.selected-product-rating {
  max-width: 240px;
}

.rating-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

::v-deep .visit-type-radio .el-radio-button__inner {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: none;
}

::v-deep .visit-type-radio.is-active .el-radio-button__inner {
  border-color: #409eff;
}

::v-deep .visit-type-radio:first-child .el-radio-button__inner {
  border-left: 1px solid #dcdfe6;
}
</style>
